import {
  Box,
  Dialog,
  DialogActions,
  DialogContent, DialogContentText,
  DialogTitle,
  FormLabel, IconButton,
  Paper,
  Typography,
  useTheme
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {
  getISODateFromDate,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import FormValidator from "../../../components/form/FormValidator";
import DatePickerValidator from "../../../components/form/DatePickerValidator";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import SnapshotIcon from '@mui/icons-material/CameraAlt';
import IconicButton from "../../../components/controls/IconicButton";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import DialogButton from "../../../components/form/DialogButton";
import Snapshot, {ISnapshotDetail, SnapshotDetail} from "../../../model/Snapshot";
import {compareAsc, endOfYear, format, max, min, startOfToday, startOfYear} from "date-fns";
import {CreateSnapshotInput, SnapshotType, UpdateSnapshotInput} from "../../../API";
import {useResources} from "../../../stores/ResourceProvider";
import {
  DeductionExpenseIcon,
  FoodAndPersonalExpenseIcon,
  GivingAndMiscExpenseIcon,
  HousingExpenseIcon,
  IncomeIcon,
  InsuranceAndMedicalExpenseIcon,
  LoanExpenseIcon,
  TaxExpenseIcon,
  TravelAndEntertainmentExpenseIcon,
  VehiclesAndTransporationExpenseIcon
} from "../../../styles/Icons";
import INetWorth from "../../../model/INetWorth";
import {globalColors} from "../../../styles/globalStyles";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {isNumber} from "../../../model/ModelUtilities";
import Schedule from "../../../model/Schedule";
import Income from "../../../model/Income";
import Deduction from "../../../model/Deduction";
import Tax from "../../../model/Tax";
import Expense, {ExpenseCategory} from "../../../model/Expense";
import IBudget from "../../../model/iBudget";
import Scheduled from "../../../model/Scheduled";
import Model from "../../../model/Model";
import EditIcon from "@mui/icons-material/Edit";
import SnapshotDetailEditDialog from "./SnapshotDetailEditDialog";

export class SnapshotEditDetail {
  id: string
  typename: string
  balance: string
  amount?: string
  schedule?: Schedule
  item?: IBudget | INetWorth

  constructor (detail: ISnapshotDetail) {
    this.id = detail.id
    this.typename = detail.typename
    this.balance = numberToMoneyFormat(detail.balance, 0)
    this.amount = isNumber(detail.amount) ? numberToMoneyFormat(detail.amount, 0) : undefined
    this.schedule = detail.schedule
  }
}

class Total {
  title: string
  count: number
  baseAmount: number
  newAmount: number
  changePct: number

  constructor () {
    this.title = ""
    this.count = 0
    this.baseAmount = 0
    this.newAmount = 0
    this.changePct = 0
  }
}

const BudgetSnapshotDialog = ({
  baseSnap,
  snapshot,
  model,
  compare,
  incomes,
  deductions,
  taxes,
  estimatedTaxes,
  expenses,
  housingExpenses,
  loanAndLiabilityExpenses,
  foodAndPersonalCareExpenses,
  insuranceAndMedicalExpenses,
  vehiclesAndTransportationExpenses,
  travelAndEntertainmentExpenses,
  givingAndMiscExpenses,
  onCancel,
  onSave,
  onDelete
}: {
  baseSnap?: Snapshot
  snapshot: Snapshot
  model: Model
  compare?: boolean
  incomes: Income[]
  deductions: Deduction[]
  taxes: Tax[]
  estimatedTaxes: Tax[]
  expenses: Expense[]
  housingExpenses: Expense[],
  loanAndLiabilityExpenses: Expense[],
  foodAndPersonalCareExpenses: Expense[],
  insuranceAndMedicalExpenses: Expense[],
  vehiclesAndTransportationExpenses: Expense[],
  travelAndEntertainmentExpenses: Expense[],
  givingAndMiscExpenses: Expense[],
  onCancel?: () => any
  onSave?: (snapshot: Snapshot) => any
  onDelete?: (snapshot: Snapshot) => any
  disableDelete?: boolean
}) => {
  const [isNew, setIsNew] = useState<boolean>(false)
  const [isComparing, setIsComparing] = useState<boolean>(false)
  const [canDelete, setCanDelete] = useState<boolean>(false)
  const [snapshotDate, setSnapshotDate] = useState<Date | undefined>()
  const [snapshotDescription, setSnapshotDescription] = useState<string>("")
  const [snapshotEditDetails, setSnapshotEditDetails] = useState<SnapshotEditDetail[]>([])
  const [editDetail, setEditDetail] = useState<SnapshotEditDetail | undefined>()
  const [snapshotType, setSnapshotType] = useState<SnapshotType>(SnapshotType.Budget)

  const { modelStore, calculator, confirm, notify } = useResources()

  useEffect(() => {
    loadSnapshot(snapshot)
  }, [snapshot])

  const loadSnapshot = (snapshot: Snapshot) => {
    setIsNew(!snapshot.id || snapshot.id === "current")
    setCanDelete(!compare && (!snapshot.id || snapshot.id !== "current"))
    setSnapshotDate(snapshot.date)
    setSnapshotDescription(snapshot.description ?? "")
    const editDetails = snapshot.details.map((detail: ISnapshotDetail) => {
      return new SnapshotEditDetail(detail)
    })
    setSnapshotEditDetails(editDetails)
    setSnapshotType(snapshot.snapshotType)
    if (compare) {
      setIsComparing(true)
    }
  }

  const theme = useTheme()

  const styles = {
    snapshotIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    actions: {
      display:"flex",
      flexDirection:"row",
      width:"100%",
      justifyContent:"space-between",
      alignItems:"center"
    },
    itemBox: {
      display:"flex",
      flexGrow:1,
      alignItems:"center",
      width: "100%",
      maxHeight:36,
      backgroundColor: theme.palette.grey["50"],
      borderWidth: 1,
      borderColor: theme.palette.background.default,
      borderStyle: "solid",
      borderRadius: 0,
      padding: 0,
      marginBottom: 1
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      paddingTop: "4px",
      color: theme.palette.grey["700"]
    },
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  const handleSave = async () => {
    if (!snapshotDate) {
      return
    }
    const details = snapshotEditDetails.map((item: SnapshotEditDetail) => {
      return new SnapshotDetail({id: item.id, typename: item.typename, balance: moneyToNumberFormat(item.balance, 0), amount: moneyToNumberFormat(item.amount), schedule: item.schedule})
    })
    let update
    // Verify there isn't already a snapshot for this date.
    const isoDate = getISODateFromDate(snapshotDate!)
    if (isNew || snapshot.date !== snapshotDate) {
      const existing = await modelStore.getSnapshotByDate(snapshot.modelId, isoDate)
      if (existing) {
        if (existing.snapshotType === SnapshotType.NetWorth) {
          // Allow merge with a NetWorth snapshot
          await mergeSnapshot(existing, details)
          return
        } else {
          notify.show('error', `A snapshot already exists for ${format(snapshotDate, "MM/dd/yyyy")}`)
          return
        }
      }
    }

    if (isNew) {
      const input: CreateSnapshotInput = {
        accountId: snapshot.accountId,
        userId: snapshot.userId,
        modelId: snapshot.modelId,
        date: isoDate,
        description: snapshotDescription,
        snapshotType: snapshotType,
        details: JSON.stringify(details)
      }

      update = await modelStore.createSnapshot(input)
    } else {
      const input: UpdateSnapshotInput = {
        id: snapshot.id,
        accountId: snapshot.accountId,
        userId: snapshot.userId,
        modelId: snapshot.modelId,
        date: getISODateFromDate(snapshotDate!),
        description: snapshotDescription,
        details: JSON.stringify(details)
      }

      update = await modelStore.updateSnapshot(input)
    }

    if (update && onSave) {
      onSave(update)
    }
  }

  const mergeSnapshot = async (existing: Snapshot, details: SnapshotDetail[]) => {
    const mergedDetails = [...existing.details, ...details]
    const input: UpdateSnapshotInput = {
      id: existing.id,
      accountId: existing.accountId,
      userId: existing.userId,
      modelId: existing.modelId,
      description: snapshotDescription,
      snapshotType: SnapshotType.Combined,
      details: JSON.stringify(mergedDetails)
    }

    const update = await modelStore.updateSnapshot(input)

    if (update && onSave) {
      onSave(update)
    }
  }

  const handleEditDetail = (editIndex: number) => {
    setEditDetail(snapshotEditDetails[editIndex])
  }

  const handleDelete = async () => {
    if (onDelete) {
      confirm.show('Confirm Delete', 'Are you sure you want to delete this snapshot?',
        ['Delete', 'Cancel'],
        async () => {
          try {
            const deleted = await modelStore.deleteSnapshot(snapshot.id)
            if (deleted) {
              onDelete(deleted)
              return true
            }
            return false
          }
          catch (err: any) {
            notify.show('error', err.message)
            return false
          }
        })
    }
  }

  const title = isNew ? "New Snapshot" : isComparing ? "Compare Snapshots" : "Edit Snapshot"

  const renderCategory = (title: string, subtotals?: Total) => {
    if (subtotals) {
      subtotals.title = `Subtotals`
      subtotals.count = 0
      subtotals.baseAmount = 0
      subtotals.newAmount = 0
      subtotals.changePct = 0
    }

    return (
      <Box display="flex" flexGrow={1} justifyContent="space-between">
        <Box display="flex" flexGrow={1}>
          <Typography variant="h4" color="primary" sx={{fontSize: 18}}>{title}</Typography>
        </Box>
        {baseSnap &&
          <Box display="flex" flexGrow={0} width={130} mr={2}>
            <FormLabel sx={styles.formLabel}>Prior Amount</FormLabel>
          </Box>
        }
        <Box display="flex" flexGrow={0} width={130} mr={2}>
          <FormLabel sx={styles.formLabel}>New Amount</FormLabel>
        </Box>
        <Box display="flex" flexGrow={0} width={30}>
        </Box>
        <Box display="flex" flexGrow={0} width={80} pl={"4px"}>
          <FormLabel sx={styles.formLabel}>Change %</FormLabel>
        </Box>
      </Box>
    )
  }

  const getAnnualBalance = (item?: IBudget, amount?: number, schedule?: Schedule, date?: Date) => {
    let balance = 0
    if (item && amount !== undefined && schedule && date) {
      const scheduled = item as unknown as Scheduled
      const snapshotDetail: ISnapshotDetail = {
        id: item.id,
        typename: item.getTypename(),
        balance: 0,
        amount: amount,
        schedule: schedule
      }

      const startYear = startOfYear(date)
      const startDate = item.startDate ? max([startYear, item.startDate]) : startYear
      const endYear = endOfYear(date)
      const endDate = item.endDate ? min([endYear, item.endDate]) : endYear
      const comp = compareAsc(startDate, endDate)

      if (comp <= 0) {
        const occurrences = calculator.getScheduledOccurrences(scheduled, startDate, endDate, snapshotDetail)
        balance = amount * occurrences.length
        // for (const {date} of occurrences) {
        //   const futureAmount = calculator.getItemFutureValue(item, date, model.inflationStrategy, snapshotDetail)
        //   balance += futureAmount
        // }
      }
    }

    return balance
  }

  const handleChangeAmount = (event: any, item: IBudget, editIndex: number) => {
    let editItem: SnapshotEditDetail
    if (editIndex < 0) {
      const typename = item.getTypename()
      editItem = new SnapshotEditDetail({id: item.id, typename: typename, balance: 0, amount: 0})
      snapshotEditDetails.push(editItem)
      editIndex = snapshotEditDetails.length - 1
    } else {
      editItem = snapshotEditDetails[editIndex]
    }
    editItem.amount = event.currentTarget.value
    setSnapshotEditDetails([...snapshotEditDetails])
  }

  const renderItem = (item: IBudget, subtotal: Total, total: Total) => {
    let icon
    let locked = false

    if (snapshotDate && item.startDate && compareAsc(item.startDate, snapshotDate) > 0) {
      return null
    }

    if (item instanceof Income) {
      icon = <IncomeIcon/>
    } else if (item instanceof Deduction) {
      icon = <DeductionExpenseIcon/>
    } else if (item instanceof Tax) {
      icon = <TaxExpenseIcon/>
      if (item.id === "estimatedFederalTaxes" && snapshot.isCurrent) {
        locked = true
      }
    } else if (item instanceof Expense) {
      switch (item.expenseCategory) {
        case ExpenseCategory.Housing:
          icon = <HousingExpenseIcon/>
          break
        case ExpenseCategory.LoansAndLiabilities:
          icon = <LoanExpenseIcon/>
          break
        case ExpenseCategory.FoodAndPersonalCare:
          icon = <FoodAndPersonalExpenseIcon/>
          break
        case ExpenseCategory.InsuranceAndMedical:
          icon = <InsuranceAndMedicalExpenseIcon/>
          break
        case ExpenseCategory.VehiclesAndTransportation:
          icon = <VehiclesAndTransporationExpenseIcon/>
          break
        case ExpenseCategory.TravelAndEntertainment:
          icon = <TravelAndEntertainmentExpenseIcon/>
          break
        case ExpenseCategory.GivingAndMisc:
          icon = <GivingAndMiscExpenseIcon/>
          break
      }
    }

    let baseAmount= 0
    let baseSchedule = Schedule.getDefaultSchedule("Monthly")
    if (baseSnap && item.startDate && compareAsc(item.startDate, baseSnap.date) <= 0) {
      const baseDetail = baseSnap.getSnapshotDetail(item)
      if (baseDetail) {
        baseAmount = baseDetail.amount ?? 0
        if (baseDetail.schedule) {
          baseSchedule = baseDetail.schedule
        }
      }
    }

    const typename = item.getTypename()
    let editIndex = snapshotEditDetails.findIndex((detail: SnapshotEditDetail) => detail.id === item.id && detail.typename === typename)
    let amount = ""
    let schedule = baseSchedule
    if (editIndex >= 0) {
      amount = snapshotEditDetails[editIndex].amount ?? "0"
      if (snapshotEditDetails[editIndex].schedule) {
        schedule = snapshotEditDetails[editIndex].schedule
      }
    } else if (item.startDate && (snapshotDate && snapshotDate.getTime() < item.startDate.getTime()) || item.archived) {
      // Don't display item if the snapshot date is before the item startDate or archived
      return null
    }

    let changeColor = globalColors.iconContrast
    let changeIcon = <ArrowDropUpIcon/>
    let changePct = ""
    let baseTotal = 0
    const annualTotal = getAnnualBalance(item, moneyToNumberFormat(amount), schedule!, snapshotDate)
    if (baseAmount !== 0) {
      baseTotal = getAnnualBalance(item, baseAmount, baseSchedule!, baseSnap!.date)
      if (baseTotal > 0) {
        changePct = numberToPercentFormat((annualTotal - baseTotal) / baseTotal, 2)
      } else if (annualTotal === baseTotal) {
        changeIcon = <span></span>
      }
    } else if (!amount) {
      changeIcon = <span></span>
    }
    if (changePct === "0%") {
      changePct = ""
      changeIcon = <span></span>
    } else if (changePct.startsWith("-")) {
      changeColor = globalColors.negative
      changeIcon = <ArrowDropDownIcon/>
      changePct = changePct.substring(1)
    } else {
      changeColor = globalColors.income
    }

    if (snapshotEditDetails.length === 0) {
      return
    }

    subtotal.count += 1
    subtotal.baseAmount += baseTotal
    subtotal.newAmount += annualTotal
    total.count += 1
    total.baseAmount += baseTotal
    total.newAmount += annualTotal

    return (
      <Box display="flex" flexGrow={1} justifyContent="stretch" key={item.id} mb={1}>
        <Box sx={styles.itemBox}>
          <Box display="flex" flexGrow={0} width={40}>
            {icon}
          </Box>
          <Box display="flex" flexGrow={1} pr={2}>
            <Typography variant="body1">{item.description}</Typography>
          </Box>
          {baseSnap &&
            <Box display="flex" flexGrow={0} width={130} mr={2}>
              <TextFieldValidator
                margin="none"
                name={`base-${item.id}`}
                label=" "
                type="text"
                fullWidth
                variant="standard"
                value={numberToMoneyFormat(baseAmount, 0)}
                helperText={`${baseSchedule?.name} (${numberToMoneyFormat(baseTotal, 0)}/yr)`}
                disabled={true}
                InputLabelProps={{shrink: false}}
              />
            </Box>
          }
          <Box display="flex" flexGrow={0} width={130} mr={2}>
            <TextFieldValidator
              margin="none"
              name={item.id}
              label=" "
              type="text"
              fullWidth
              variant="standard"
              required
              validators={{ required: false, isMoney: true }}
              value={amount}
              helperText={`${schedule?.name} (${numberToMoneyFormat(annualTotal, 0)}/yr)`}
              disabled={locked}
              InputLabelProps={{shrink: false}}
              onChange={(event: any) => handleChangeAmount(event, item, editIndex)}
            />
          </Box>
          <Box display="flex" flexGrow={0} width={30}>
            <IconButton id={`${item.id}-edit-btn`}size="small"
                        sx={{paddingTop:"4px", paddingBottom:0}}
                        onClick={(event: any) => {
                          handleEditDetail(editIndex)
                        }}
            >
              <EditIcon sx={{width:18, height:18}}/>
            </IconButton>
          </Box>
          <Box display="flex" flexGrow={0} width={80} color={changeColor} pt={1.5} whiteSpace="nowrap">
            {changeIcon} <Typography variant="body2" color={changeColor}>{changePct}</Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const renderSubtotal = (subtotal: Total) => {
    if (subtotal.count <= 0) {
      return null
    }

    let baseAmount = subtotal.baseAmount
    let amount = subtotal.newAmount

    let changeColor = globalColors.iconContrast
    let changeIcon = <ArrowDropUpIcon/>
    let changePct = ""
    if (baseAmount > 0) {
      const change = (amount - baseAmount) / baseAmount
      changePct = numberToPercentFormat(change, change > -1 && change < 1 ? 2 : 0)
    } else if (!amount) {
      changeIcon = <span></span>
    }
    if (changePct === "0%") {
      changePct = ""
      changeIcon = <span></span>
    } else if (changePct.startsWith("-")) {
      changeColor = globalColors.negative
      changeIcon = <ArrowDropDownIcon/>
      changePct = changePct.substring(1)
    } else {
      changeColor = globalColors.income
    }


    return (
      <Box display="flex" flexGrow={1} justifyContent="space-between" mb={1}>
        <Box display="flex" flexGrow={0} width={40}>
        </Box>
        <Box display="flex" flexGrow={1}>
          <Typography variant="h5">Subtotals</Typography>
        </Box>
        {baseSnap &&
          <Box display="flex" flexGrow={0} width={130} mr={2}>
            <Typography variant="h5">{numberToMoneyFormat(subtotal.baseAmount, 0)}/yr</Typography>
          </Box>
        }
        <Box display="flex" flexGrow={0} width={130} mr={2}>
          <Typography variant="h5">{numberToMoneyFormat(subtotal.newAmount, 0)}/yr</Typography>
        </Box>
        <Box display="flex" flexGrow={0} width={30}>
        </Box>
        <Box display="flex" flexGrow={0} width={80} color={changeColor} whiteSpace="nowrap">
          {changeIcon} <Typography variant="h5" color={changeColor}>{changePct}</Typography>
        </Box>
      </Box>
    )
  }

  const renderTotal = (total: Total) => {
    let baseAmount = total.baseAmount
    let amount = total.newAmount

    let changeColor = globalColors.iconContrast
    let changeIcon = <ArrowDropUpIcon/>
    let changePct = ""
    if (baseAmount > 0) {
      const change = (amount - baseAmount) / baseAmount
      changePct = numberToPercentFormat(change, change > -1 && change < 1 ? 2 : 0)
    } else if (!amount) {
      changeIcon = <span></span>
    }
    if (changePct === "0%") {
      changePct = ""
      changeIcon = <span></span>
    } else if (changePct.startsWith("-")) {
      changeColor = globalColors.negative
      changeIcon = <ArrowDropDownIcon/>
      changePct = changePct.substring(1)
    } else {
      changeColor = globalColors.income
    }


    return (
      <Box display="flex" flexGrow={1} justifyContent="space-between" mb={1}>
        <Box display="flex" flexGrow={0} width={40}>
        </Box>
        <Box display="flex" flexGrow={1}>
          <Typography variant="h5">{total.title}</Typography>
        </Box>
        {baseSnap &&
          <Box display="flex" flexGrow={0} width={130} mr={2}>
            <Typography variant="h5">{numberToMoneyFormat(total.baseAmount, 0)}/yr</Typography>
          </Box>
        }
        <Box display="flex" flexGrow={0} width={130} mr={2}>
          <Typography variant="h5">{numberToMoneyFormat(total.newAmount, 0)}/yr</Typography>
        </Box>
        <Box display="flex" flexGrow={0} width={30}>
        </Box>
        <Box display="flex" flexGrow={0} width={80} color={changeColor} whiteSpace="nowrap">
          {changeIcon} <Typography variant="h5" color={changeColor}>{changePct}</Typography>
        </Box>
      </Box>
    )
  }

  const renderTotals = (totals: Total[]) => {
    const net = totals[5]
    const taxTotal = totals[3].newAmount !== 0 ? totals[3] : totals[2]
    net.baseAmount = totals[0].baseAmount - totals[1].baseAmount - taxTotal.baseAmount - totals[4].baseAmount
    net.newAmount = totals[0].newAmount - totals[1].newAmount - taxTotal.newAmount - totals[4].newAmount
    return totals.map((total: Total) => renderTotal(total))
  }
  
  const maxDate = startOfToday()
  let subtotals: Total = new Total()
  let totals: Total[] = [
    {title: "Incomes", count: 0, baseAmount: 0, newAmount: 0, changePct: 0},
    {title: "Deductions & Contributions", count: 0, baseAmount: 0, newAmount: 0, changePct: 0},
    {title: "Tax Withholdings", count: 0, baseAmount: 0, newAmount: 0, changePct: 0},
    {title: "Estimated Taxes", count: 0, baseAmount: 0, newAmount: 0, changePct: 0},
    {title: "Expenses", count: 0, baseAmount: 0, newAmount: 0, changePct: 0},
    {title: "Surplus/Shortfall", count: 0, baseAmount: 0, newAmount: 0, changePct: 0},
  ]

  return (
    <Dialog
      id="budgetSnapshotDialog"
      open={true}
      onClose={handleCancel}
      scroll="paper"
      maxWidth="md"
      fullWidth
      aria-labelledby="budgetSnapshotDialogTitle"
    >
      <Paper sx={{width:"100%"}}>
        <FormValidator onSubmit={handleSave} name="snapshotForm" id="snapshotForm">
          <DialogTitle id="budgetSnapshotDialogTitle">
            {title}
          </DialogTitle>
          <DialogContent sx={{ my:1, p:1, maxHeight:"calc(100vh - 206px)", overflowY:"scroll"}}>
            <Box display="flex" flexGrow={1} justifyContent="stretch">
              <Box display="flex" flexGrow={1} width="100%">
                <Box display="flex" flexGrow={0} width={40} alignItems="center">
                  <SnapshotIcon style={styles.snapshotIcon}/>
                </Box>
                <Box display="flex" flexGrow={1} pr={2}>
                  <TextFieldValidator
                    margin="none"
                    name="snapshotDescription"
                    label="Description"
                    type="text"
                    fullWidth
                    variant="standard"
                    validators={{ required: false }}
                    value={snapshotDescription}
                    onChange={(event: any) => setSnapshotDescription(event.target.value)}
                  />
                </Box>
                {baseSnap &&
                  <Box display="flex" flexGrow={0} width={130} mr={2}>
                    <DatePickerValidator
                      name="baseDate"
                      label="Prior Date"
                      value={baseSnap.date}
                      format="MM-dd-yyyy"
                      variant="standard"
                      margin="dense"
                      disabled={true}
                    />
                  </Box>
                }
                <Box display="flex" flexGrow={0} width={130} mr={2}>
                  <DatePickerValidator
                    name="snapshotDate"
                    label="New Date"
                    value={snapshotDate}
                    format="MM-dd-yyyy"
                    variant="standard"
                    margin="dense"
                    maxDate={maxDate}
                    required
                    disabled={!canDelete}
                    onChange={(newValue: any) => {
                      // TODO: Validation for valid dates
                      setSnapshotDate(newValue)
                    }}
                  />
                </Box>
                <Box display="flex" flexGrow={0} width={115}></Box>
              </Box>
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Incomes", subtotals)}
              {incomes.map((income: Income) => renderItem(income, subtotals, totals[0]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Deductions & Contributions", subtotals)}
              {deductions.map((deduction: Deduction) => renderItem(deduction, subtotals, totals[1]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Tax Withholdings", subtotals)}
              {taxes.map((tax: Tax) => renderItem(tax, subtotals, totals[2]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Estimated Taxes", subtotals)}
              {estimatedTaxes.map((tax: Tax) => renderItem(tax, subtotals, totals[3]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Housing", subtotals)}
              {housingExpenses.map((expense: Expense) => renderItem(expense, subtotals, totals[4]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Loans & Liabilities", subtotals)}
              {loanAndLiabilityExpenses.map((expense: Expense) => renderItem(expense, subtotals, totals[4]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Food & Personal Care", subtotals)}
              {foodAndPersonalCareExpenses.map((expense: Expense) => renderItem(expense, subtotals, totals[4]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Insurance & Medical", subtotals)}
              {insuranceAndMedicalExpenses.map((expense: Expense) => renderItem(expense, subtotals, totals[4]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Vehicles & Transportation", subtotals)}
              {vehiclesAndTransportationExpenses.map((expense: Expense) => renderItem(expense, subtotals, totals[4]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Travel & Entertainment", subtotals)}
              {travelAndEntertainmentExpenses.map((expense: Expense) => renderItem(expense, subtotals, totals[4]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Giving & Misc", subtotals)}
              {givingAndMiscExpenses.map((expense: Expense) => renderItem(expense, subtotals, totals[4]))}
              {renderSubtotal(subtotals)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column" bgcolor={globalColors.details}>
              {renderCategory("Totals")}
              {renderTotals(totals)}
            </Box>
            <DialogContentText sx={{pt:1}}>
              * Note: If some budget items are not listed, the item start date may be later than the snapshot date.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box mt={1} sx={styles.actions}>
              <Box>
                {onDelete && canDelete &&
                  <IconicButton onClick={handleDelete}>
                    <DeleteIcon/>
                  </IconicButton>
                }
              </Box>
              <Box display="flex" flexWrap="nowrap">
                <DialogButton variant="secondary" type="button" onClick={handleCancel}>
                  Cancel
                </DialogButton>
                <Box width={16}/>
                <DialogButton variant="primary" type="submit">
                  Save
                </DialogButton>
              </Box>
            </Box>
          </DialogActions>
        </FormValidator>
      </Paper>
      {editDetail &&
        <SnapshotDetailEditDialog detail={editDetail}
                                  open={true}
                                  onSave={(detail: SnapshotEditDetail) => {
                                    const index = snapshotEditDetails.findIndex((item: SnapshotEditDetail) => item.id === detail.id && item.typename === detail.typename)
                                    if (index >= 0) {
                                      snapshotEditDetails[index] = detail
                                      setSnapshotEditDetails([...snapshotEditDetails])
                                    }
                                    setEditDetail(undefined)
                                  }}
                                  onClose={() => setEditDetail(undefined)}
        />
      }
    </Dialog>
  )

}

export default BudgetSnapshotDialog